/**
 * Block B Phase 6 (2026-06-01) — Complete rewrite.
 *
 * Стара версія (Phase 3 Stage 1) — статична інструкція з 5 секцій.
 * Нова версія — landing з 3 чіткими шляхами (wizard / continue / reverse)
 * + посилання на 1С обробку + KB FAQ.
 */
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { migrationRuns } from '@/api/migrationRuns';

export default function MigrationFromOneCPage() {
  const runsList = useQuery({
    queryKey: ['migration-runs'],
    queryFn: () => migrationRuns.list(),
  });

  const activeRuns = (runsList.data ?? []).filter(
    (r) => !['COMPLETED', 'FAILED', 'ROLLED_BACK'].includes(r.status),
  );
  const completedRuns = (runsList.data ?? []).filter(
    (r) => r.status === 'COMPLETED',
  );

  return (
    <div style={{ maxWidth: 1100, margin: '1rem auto', padding: '1rem' }}>
      <div style={{ marginBottom: '1.5rem' }}>
        <h1>Міграція з 1С / BAS</h1>
        <p style={{ color: 'var(--muted)', fontSize: '1.05em' }}>
          Перенесення обліку з <strong>4 типових конфігурацій</strong> у наш
          сервіс через одну 1С обробку: <strong>1С УТП</strong>,
          <strong> 1С БП України</strong>, <strong>BAS Бухгалтерія</strong>,
          <strong> 1С УТ / BAS УТ</strong>. Обробка автоматично визначає
          конфігурацію. 30-денне вікно зворотньої міграції — назад у ту
          ж конфігурацію.
        </p>
      </div>

      {/* Status Banner */}
      <StatusBanner
        activeCount={activeRuns.length}
        completedCount={completedRuns.length}
      />

      {/* Three Paths */}
      <h2 style={{ marginTop: '2rem' }}>Що хочете зробити?</h2>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: '1rem',
          marginTop: '1rem',
        }}
      >
        <PathCard
          icon="🚀"
          title="Новий wizard міграції"
          description="Якщо ви ще НЕ мігрували — почніть тут. 6-step майстер: дата, профіль, глибини, підтвердження, Run ID для 1С, прогрес."
          linkTo="/migration/wizard"
          linkText="Старт wizard →"
          variant="primary"
        />
        <PathCard
          icon="📋"
          title="Продовжити existing run"
          description={
            activeRuns.length > 0
              ? `У вас ${activeRuns.length} активний(их) wizard run(s). Поверніться щоб продовжити.`
              : 'Якщо ви почали wizard раніше — продовжте з місця зупинки.'
          }
          linkTo="/migration/runs"
          linkText="Iсторія міграцій →"
          variant={activeRuns.length > 0 ? 'warning' : 'secondary'}
        />
        <PathCard
          icon="⬅"
          title="Зворотна міграція у 1С/BAS"
          description={
            completedRuns.length > 0
              ? `${completedRuns.length} завершен(их) міграцій. У межах 30 днів — назад у ту саму конфігурацію (БП/BAS Бух/УТП/УТ).`
              : 'Експорт даних назад у формат вашої 1С/BAS. Доступно 30 днів від COMPLETED. Закон 5: тільки туди звідки прийшли.'
          }
          linkTo="/migration/runs"
          linkText="Список з reverse export →"
          variant={completedRuns.length > 0 ? 'success' : 'secondary'}
        />
      </div>

      {/* Resources */}
      <Resources />

      {/* FAQ */}
      <FAQ />
    </div>
  );
}

interface StatusBannerProps {
  activeCount: number;
  completedCount: number;
}

function StatusBanner({ activeCount, completedCount }: StatusBannerProps) {
  if (activeCount === 0 && completedCount === 0) {
    return (
      <div
        style={{
          padding: '1rem',
          background: '#f0f8ff',
          border: '1px solid #06c',
          borderRadius: 8,
        }}
      >
        <strong>👋 Перша міграція?</strong>
        <p style={{ margin: '0.5rem 0 0' }}>
          Почніть з <Link to="/migration/wizard">wizard майстра</Link> —
          ми проведемо вас за 6 кроків. Тривалість wizard: 5-10 хвилин.
        </p>
      </div>
    );
  }

  if (activeCount > 0) {
    return (
      <div
        style={{
          padding: '1rem',
          background: '#fff9cc',
          border: '1px solid #e5b800',
          borderRadius: 8,
        }}
      >
        <strong>⏳ Активна міграція</strong>
        <p style={{ margin: '0.5rem 0 0' }}>
          У вас {activeCount} незавершен(их) wizard run(s).{' '}
          <Link to="/migration/runs">Подивитись список →</Link>
        </p>
      </div>
    );
  }

  return (
    <div
      style={{
        padding: '1rem',
        background: '#d4f4d4',
        border: '1px solid #0a8',
        borderRadius: 8,
      }}
    >
      <strong>✅ {completedRuns(completedCount)}</strong>
      <p style={{ margin: '0.5rem 0 0' }}>
        Ви успішно мігрували. 30-денне вікно зворотньої міграції доступне
        у <Link to="/migration/runs">списку міграцій</Link>.
      </p>
    </div>
  );
}

function completedRuns(n: number): string {
  if (n === 1) return '1 завершена міграція';
  if (n < 5) return `${n} завершені міграції`;
  return `${n} завершених міграцій`;
}

interface PathCardProps {
  icon: string;
  title: string;
  description: string;
  linkTo: string;
  linkText: string;
  variant: 'primary' | 'success' | 'warning' | 'secondary';
}

function PathCard({ icon, title, description, linkTo, linkText, variant }: PathCardProps) {
  const colors: Record<typeof variant, { border: string; bg: string }> = {
    primary: { border: '#06c', bg: '#f0f8ff' },
    success: { border: '#0a8', bg: '#d4f4d4' },
    warning: { border: '#e5b800', bg: '#fff9cc' },
    secondary: { border: '#ccc', bg: '#f9f9f9' },
  };
  const c = colors[variant];
  return (
    <div
      style={{
        padding: '1rem',
        background: c.bg,
        border: `1px solid ${c.border}`,
        borderRadius: 8,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div style={{ fontSize: '2em', marginBottom: '0.5rem' }}>{icon}</div>
      <h3 style={{ margin: '0 0 0.5rem' }}>{title}</h3>
      <p style={{ flex: 1, fontSize: '0.9em', color: 'var(--muted)' }}>
        {description}
      </p>
      <Link to={linkTo} style={{ marginTop: '0.5rem' }}>
        <button className="primary" style={{ width: '100%' }}>
          {linkText}
        </button>
      </Link>
    </div>
  );
}

function Resources() {
  return (
    <div style={{ marginTop: '2rem' }}>
      <h2>Ресурси</h2>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: '1rem',
        }}
      >
        <ResourceCard
          title="📥 1С/BAS обробка — Керовані форми"
          description="Для 1С 8.3 тонкий/веб-клієнт або сервер. Підтримує 4 конфігурації (УТП / БП / BAS Бух / BAS УТ) — обробка автодетектує."
          links={[
            {
              href: '/migration-1c/FormProcessing_FormModule.txt',
              label: 'Модуль форми (основний код, 96 секцій)',
              download: 'FormProcessing_FormModule.txt',
            },
            {
              href: '/migration-1c/ObjectModule.txt',
              label: 'Модуль об\'єкта',
              download: 'ObjectModule.txt',
            },
            {
              href: '/migration-1c/README.md',
              label: 'Iнструкція встановлення (керовані)',
              download: 'README.md',
            },
          ]}
        />
        <ResourceCard
          title="📥 1С/BAS обробка — Обычные формы"
          description="Для 1С 8.2 / 8.3 толстый клиент у файловому режимі (локальна без серверних). Та сама логіка, інший синтаксис форми."
          links={[
            {
              href: '/migration-1c/ObycnayaForma_FormModule.txt',
              label: 'Модуль форми (обычная)',
              download: 'ObycnayaForma_FormModule.txt',
            },
            {
              href: '/migration-1c/ObycnayaForma_ObjectModule.txt',
              label: 'Модуль об\'єкта (обычная)',
              download: 'ObycnayaForma_ObjectModule.txt',
            },
            {
              href: '/migration-1c/README_OBYCHNAYA.md',
              label: 'Iнструкція встановлення (обычные)',
              download: 'README_OBYCHNAYA.md',
            },
          ]}
        />
        <ResourceCard
          title="❓ Питання-відповіді"
          description="Найчастіші питання про міграцію — нижче на цій сторінці."
          links={[
            { href: '#faq', label: 'Перейти до FAQ ↓' },
          ]}
        />
        <ResourceCard
          title="⚙️ API Endpoints"
          description="Технічна документація API для розробників."
          links={[
            { href: '/docs', label: 'OpenAPI / Swagger UI' },
          ]}
        />
      </div>
    </div>
  );
}

interface ResourceCardProps {
  title: string;
  description: string;
  links: { href: string; label: string; download?: string }[];
}

function ResourceCard({ title, description, links }: ResourceCardProps) {
  return (
    <div
      style={{
        padding: '1rem',
        background: '#fafafa',
        border: '1px solid #ddd',
        borderRadius: 8,
      }}
    >
      <h3 style={{ margin: '0 0 0.5rem', fontSize: '1.05em' }}>{title}</h3>
      <p style={{ fontSize: '0.9em', color: 'var(--muted)' }}>{description}</p>
      <ul style={{ paddingLeft: '1.2rem', margin: '0.5rem 0' }}>
        {links.map((l) => (
          <li key={l.href}>
            <a
              href={l.href}
              {...(l.download
                ? { download: l.download }
                : { target: '_blank', rel: 'noopener noreferrer' })}
            >
              {l.download ? '📥 ' : ''}{l.label}
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
}

function FAQ() {
  return (
    <div id="faq" style={{ marginTop: '2rem' }}>
      <h2>Часто задавані питання</h2>

      <FAQItem
        question="З яких конфігурацій 1С/BAS можна мігрувати?"
        answer={
          <>
            <strong>4 конфігурації</strong>: 1С УТП (Управління Торговим
            Підприємством), 1С БП України (Бухгалтерія 2.x), BAS Бухгалтерія
            (наступник БП після 2022), 1С УТ / BAS УТ (Управління Торгівлею).
            Наша обробка <code>.epf</code> автоматично визначає у якій
            конфігурації її запустили — через ім'я метаданих 1С. Якщо
            конфігурація перейменована або кастомна — на формі є поле{' '}
            <strong>SourceОверайд</strong> для ручного вибору.
          </>
        }
      />

      <FAQItem
        question="Скільки часу займе вся міграція?"
        answer={
          <>
            Залежить від обсягу: ФОП-самозайнятий — 10-15 хв, малий бізнес — 30-60
            хв, середній — 1-2 години, велике підприємство (виробництво) — 2-4
            години. Сама 1С/BAS обробка експорту — десь стільки ж, паралельно з
            нашими операціями.
          </>
        }
      />

      <FAQItem
        question="Чи можу я повернутись у 1С/BAS після міграції?"
        answer={
          <>
            <strong>Так, протягом 30 днів</strong> після завершення міграції
            (status=COMPLETED). У <Link to="/migration/runs">списку міграцій</Link>{' '}
            на кожній COMPLETED — кнопка <strong>«⬅ Експорт назад у [ваша
            конфігурація]»</strong> (точна назва підставляється з вашого джерела).
            Отримаєте <code>.zip</code> пакет з усіма даними у форматі тієї ж
            конфігурації звідки ви прийшли.
          </>
        }
      />

      <FAQItem
        question="Чи можу я мігрувати з БП а потім експортувати у BAS Бух?"
        answer={
          <>
            <strong>Ні.</strong> <strong>Закон 5</strong> нашого сервісу:
            зворотна міграція <em>тільки туди звідки прийшли</em>. Якщо мігрували
            з БП — реверс тільки у БП. Спроба обрати інший формат блокується
            <strong> 403 Forbidden</strong>. Причина: формати документів різні
            (наприклад, у BAS Бух «Відомість на виплату» має 4 варіанти, а у БП
            — один). Перетворення «БП → BAS» зламає коректне читання у вашій базі.
          </>
        }
      />

      <FAQItem
        question="Які зарплатні документи переносяться?"
        answer={
          <>
            <strong>За останні 12-24 місяці</strong> (залежно від профілю):
            розрахунково-платіжні відомості, відомості на виплату (банк/каса/
            перерахуванням), накази на відпустку + розрахунок відпускних, лікарняні
            + розрахунок, авансові звіти. Працівники, фізособи, підрозділи, посади
            і початкові залишки по 661/651 — теж. <strong>Не переносяться</strong>:
            планові нарахування, військовий облік, індексація ЗП, депонована
            зарплата — залишаються в архіві 1С/BAS.
          </>
        }
      />

      <FAQItem
        question="А накази по кадрах (прийняття, звільнення, переведення)?"
        answer={
          <>
            За замовчуванням <strong>не переносяться історично</strong> —
            мігруємо тільки активних працівників. Якщо потрібна історія наказів
            (наприклад, для аудитора) — увімкніть <code>hr_orders_months</code>{' '}
            у custom-профілі wizard.
          </>
        }
      />

      <FAQItem
        question="Чи мігровані числа з 1С зміняться у нашому сервісі?"
        answer={
          <>
            <strong>Ні</strong>. ПДФО, ВЗ, ЄСВ, ПСП у мігрованих документах —
            точно ті ж числа, що були у вашій 1С/BAS. Ми <em>не перераховуємо</em>{' '}
            мігровані нарахування. Причина: звітність у ДПС за минулі періоди
            уже подана з цими сумами, перерахунок розірвав би узгодження. Якщо
            у 1С/BAS нарахування неправильне — виправте там, потім повторіть
            міграцію (не правте вручну у нашому сервісі).
          </>
        }
      />

      <FAQItem
        question="Чи будуть нові нарахування (зроблені у нашому сервісі) такі ж як рахує 1С/BAS?"
        answer={
          <>
            <strong>Переважно так, можливі копійчані відмінності.</strong>
            Прості формули (ПДФО 18%, ВЗ 1.5%) — точно той самий результат.
            Складні (середній заробіток для відпустки/лікарняного, FIFO
            собівартість) — у межах <strong>±0.5%</strong>. Причини: різниці
            у календарі свят (наш — актуальний на 2026, у 1С/BAS може бути
            застарілий), у мінімальній зарплаті та прожитковому мінімумі (ми
            оновлюємо одразу при змінах закону), у HALF_UP-округленнях. Це не
            штраф — це нормальна ситуація з multi-source системами.
          </>
        }
      />

      <FAQItem
        question="Що буде з 1С базою після міграції?"
        answer={
          <>
            <strong>Нічого</strong>. Наша обробка тільки <em>читає</em> з 1С —
            нічого не пише, не видаляє. Ваша 1С база залишається в тому ж стані
            як до міграції. Завжди тримайте backup на випадок інших помилок.
          </>
        }
      />

      <FAQItem
        question="Скільки коштує міграція?"
        answer={
          <>
            Міграція <strong>безкоштовна</strong> для нових клієнтів. Платите
            тільки за щомісячне використання сервісу після переходу. Reverse
            export також безкоштовний — це частина наших гарантій.
          </>
        }
      />

      <FAQItem
        question="Що якщо контрольна сума не співпадає?"
        answer={
          <>
            Sentinel-перевірки post-bulk-POST детектують розбіжності і зупиняють
            міграцію зі статусом FAILED. У звіті побачите деталі. Зверніться у
            support — переважно це через «брудний» стан 1С/BAS (незакриті
            проводки, від'ємні залишки). Виправте у 1С/BAS → повторіть міграцію.
          </>
        }
      />

      <FAQItem
        question="Що означає банер «Архів у 1С/BAS» який я бачу на сторінках?"
        answer={
          <>
            Це нагадування про <strong>частину історії що залишилась у вашій
            оригінальній 1С/BAS-базі</strong>. Ми переносимо тільки дані за
            період window (за вашим вибором у Wizard — наприклад, 12 місяців).
            Решта історії — лишається у архіві. Якщо клієнт чи аудитор питає
            «де документ за 2 роки тому» — відкривайте 1С/BAS. Банер показується
            тільки якщо ваша організація має <em>migration_source_config</em>
            (мігрувала з 1С/BAS). Сховати на × у куті — до кінця сесії.
          </>
        }
      />

      <FAQItem
        question="У мене дві бази: БП і УТП. Можна мігрувати з обох?"
        answer={
          <>
            <strong>Так, але як дві окремі організації</strong> у нашому сервісі.
            Створіть першу organization, мігруйте з БП → отримаєте «Архів у БП».
            Потім другу organization, мігруйте з УТП → отримаєте «Архів у УТП».
            Це нормальний flow для холдингу з кількома юрособами. Кожна
            організація має свій reverse-export — у свою конфігурацію.
          </>
        }
      />
    </div>
  );
}

interface FAQItemProps {
  question: string;
  answer: React.ReactNode;
}

function FAQItem({ question, answer }: FAQItemProps) {
  return (
    <details style={{ marginBottom: '0.75rem' }}>
      <summary style={{ cursor: 'pointer', fontWeight: 'bold', padding: '0.5rem' }}>
        {question}
      </summary>
      <div style={{ padding: '0.5rem 1rem', color: 'var(--muted)' }}>{answer}</div>
    </details>
  );
}
